<template>
  <section
    class="search-results"
    :class="[
      { desktop: $isDesktop },
      { fetching: isSearchSubmitting },
      { 'no-search-cards': campsites.length === 0 },
    ]"
  >
    <translate
      v-if="showPaginationSummary"
      tag="p"
      class="page-count-summary"
      data-test="page-count-summary"
      :tt="{ currentPage, totalPages }"
    >
      Page {currentPage} of {totalPages}
    </translate>

    <div v-if="$isDesktop && campsites.length === 0" class="loading-spinner">
      <loading-spinner location="search-results-list" />
    </div>

    <ul
      data-test="search-results"
      :class="{ 'search-results-list': campsites.length }"
    >
      <template v-for="(campsite, index) in campsites" :key="campsite.slug">
        <li
          v-if="isCurrentCampsiteFirstZeroAvailabilityOnTheList(index)"
          :key="`zero-availability-listing-message-${index}`"
          class="basic-listing-message"
        >
          <translate>
            The following campsites have no availability at present on
            Pitchup.com...
          </translate>
        </li>
        <li
          v-if="isCurrentCampsiteFirstNonBookableOnTheList(index)"
          :key="`basic-listing-message-${index}`"
          class="basic-listing-message"
        >
          <translate>
            The following campsites and holiday parks aren't bookable through
            Pitchup.com...
          </translate>
        </li>
        <li
          v-observe-visibility="{
            callback: visibilityChanged,
            throttle: 300,
            intersection: {
              threshold: 0.65,
            },
            throttleOptions: {
              leading: 'both',
            },
          }"
          class="search-card"
          :class="{ 'basic-listing': campsite.status !== 'bookable' }"
          :data-slug="campsite.slug"
          :data-point="campsite.point"
          @mouseenter.passive="handleMouseEnter(campsite)"
          @mouseleave.passive="handleMouseLeave()"
        >
          <pu-campsite-link
            :hierarchy-path="campsite.hierarchyPath"
            :slug="campsite.slug"
            exclude-type="fullMinusType"
            :target="$isDesktop ? '_blank' : undefined"
            :prefetch="!$isDesktop"
            :filters="{ facets: cardFacets }"
            :class="{ 'search-card-clicked': clickedItem === campsite.slug }"
            @click.passive="handleSearchResultLinkClick(campsite.slug, $event)"
          >
            <pu-campsite-price-card
              :search-result-index="index"
              v-bind="campsiteToCampsitePriceCardProps(campsite)"
            />
          </pu-campsite-link>
        </li>
      </template>
    </ul>

    <template v-if="showPaginationControls && paginationData">
      <slot name="pagination-controls">
        <AsyncSearchResultsPaginationControls
          :key="getAsyncComponentKey('paginationControls')"
          @link-clicked="scrollToTop"
          :pagination-data="paginationData"
          :search-filters="searchFilters"
          @vue:mounted="setAsyncComponentKey('paginationControls')"
        />
      </slot>
    </template>
    <nuxt-to-meta :nuxt2meta="head" />
  </section>
</template>

<script lang="ts">
import { defineAsyncComponent } from 'vue'
import PuCampsitePriceCard from '~/components/campsite/campsite-card/PuCampsitePriceCard.vue'
import type { PaginationData } from '~/utility/pagination'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { hydrateOnVisibleSafe } from '../../../utility/hydration/hydrateOnVisibleSafe'
import { logger } from '../../../utility/logger'
import { defaultPageSize } from '../defaults'
import type { CampsiteResult, SearchFilters } from '../schemas'

const AsyncSearchResultsPaginationControls = defineAsyncComponent({
  loader: () => import('./SearchResultsPaginationControls.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

@Component({
  components: {
    PuCampsitePriceCard,
    AsyncSearchResultsPaginationControls,
  },
  emits: ['visibleCampsiteChange', 'scrolledResultsDown'],
  setup() {
    const searchStore = useSearchStoreSetup(usePinia())
    const seenCampsitesStore = useSeenCampsitesStore(usePinia())
    const currencyStore = useCurrencyStore(usePinia())
    const { $isDesktop } = useNuxtApp()

    if ($isDesktop) {
      useHead({
        script: [
          {
            innerHTML: `{
            "prefetch": [{
              "where": {"selector_matches": ".search-card a[target=_blank]"},
              "eagerness": "moderate"
            }]
          }`,
            type: 'speculationrules',
            tagPosition: 'bodyClose',
          },
        ],
      })
    }

    return { searchStore, seenCampsitesStore, currencyStore }
  },
})
class SearchResultsList extends Vue {
  @Prop()
  paginationData?: PaginationData

  @Prop()
  campsites: CampsiteResult[]

  @Prop()
  searchFilters: SearchFilters

  @Prop()
  isPointSearch: boolean

  searchStore: ReturnType<typeof useSearchStoreSetup>
  seenCampsitesStore: ReturnType<typeof useSeenCampsitesStore>
  currencyStore: ReturnType<typeof useCurrencyStore>
  visibleCampsites: Set<string> = new Set()
  hoverTimeout: number | null = null
  hoverIdleCallbackId: number | null = null
  hoverCooldown: boolean = false
  currentHoverCampsite: CampsiteResult | null = null
  clickedItem = ''
  _isUnmounting: boolean = false
  _isMounted: boolean = false

  asyncComponentKeys: Record<string, string> = {}

  // Helper methods for async component keys
  getAsyncComponentKey(componentName: string): string {
    return (
      this.asyncComponentKeys[componentName] ||
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
    )
  }

  setAsyncComponentKey(componentName: string): void {
    this.asyncComponentKeys[componentName] =
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
  }

  mounted() {
    this._isMounted = true
  }

  onMouseEnterCampsite(campsite: CampsiteResult) {
    // Don't emit if component is unmounting or not mounted
    if (this._isUnmounting || !this._isMounted) {
      return
    }

    logger('SearchResultsList.onMouseEnterCampsite', campsite)
    this.$emit('visibleCampsiteChange', {
      slug: campsite.slug,
      point: campsite.point,
    })
  }

  handleMouseEnter(campsite: CampsiteResult) {
    // Don't execute if component is unmounting or not mounted
    if (this._isUnmounting || !this._isMounted) {
      return
    }

    // Cancel any pending idle callback
    if (this.hoverIdleCallbackId) {
      cancelIdleCallback(this.hoverIdleCallbackId)
    }

    this.hoverIdleCallbackId = requestIdleCallback(() => {
      // Don't execute if component is unmounting
      if (this._isUnmounting || !this._isMounted) {
        return
      }

      if (this.hoverCooldown) {
        this.currentHoverCampsite = campsite
        return
      }
      this.onMouseEnterCampsite(campsite)
      this.currentHoverCampsite = null
      this.hoverIdleCallbackId = null
    })
  }

  handleMouseLeave() {
    // Don't execute if component is unmounting or not mounted
    if (this._isUnmounting || !this._isMounted) {
      return
    }

    this.currentHoverCampsite = null
    this.startHoverCooldown()
  }

  startHoverCooldown() {
    // Don't execute if component is unmounting or not mounted
    if (this._isUnmounting || !this._isMounted) {
      return
    }

    // Clear any existing timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
    }

    this.hoverCooldown = true
    this.hoverTimeout = window.setTimeout(() => {
      // Don't execute if component is unmounting or not mounted
      if (this._isUnmounting || !this._isMounted) {
        return
      }

      this.hoverCooldown = false
      if (this.currentHoverCampsite) {
        this.handleMouseEnter(this.currentHoverCampsite)
      }
      this.hoverTimeout = null
    }, 300)
  }

  handleSearchResultLinkClick(campsiteSlug, $event) {
    if (
      !$event.target.classList.contains('subsection-open-txt') &&
      !$event.target.classList.contains('subsection-close-txt') &&
      !$event.target.classList.contains('show-more-pitches') &&
      !$event.target.classList.contains('favourite-link')
    ) {
      this.applyClickedClass(campsiteSlug)
    }
    requestIdleCallback(() => this.seenCampsitesStore.add(campsiteSlug))
  }

  applyClickedClass(slug) {
    this.clickedItem = slug
    setTimeout(() => {
      this.clickedItem = ''
    }, 500)
  }

  get nonBookableCampsitesCount() {
    return this.campsites.filter(
      (cs) => cs.status === 'free' && !cs.availableToSearch,
    ).length
  }

  get zeroAvailabilityCampsitesCount() {
    return this.campsites.filter((cs) => cs.bookable && !cs.availableToSearch)
      .length
  }

  isCurrentCampsiteFirstNonBookableOnTheList(index: number) {
    return (
      this.nonBookableCampsitesCount &&
      this.campsites[index - 1] &&
      this.campsites[index - 1].bookable &&
      !this.campsites[index].bookable
    )
  }

  isCurrentCampsiteFirstZeroAvailabilityOnTheList(index: number) {
    return (
      this.zeroAvailabilityCampsitesCount &&
      this.campsites[index - 1] &&
      this.campsites[index - 1].availableToSearch &&
      !this.campsites[index].availableToSearch
    )
  }

  scrollToTop() {
    window.scrollTo(0, 0)
  }

  get cardFacets() {
    return this.searchStore.context.results?.pitchtypeFacets || []
  }

  get query() {
    const removeParams = ['page', 'point', 'h', 'hierarchy']
    const remove = removeParams.reduce((a, b) => (a[b] = undefined) || a, {})
    return Object.assign({}, this.$route.query, remove)
  }

  get showPaginationControls() {
    return (
      this.paginationData &&
      this.paginationData.totalCount >
        (this.searchFilters.limit || defaultPageSize)
    )
  }

  get isSearchSubmitting() {
    return ['searching', 'priceSearching'].some((state) =>
      this.searchStore.states.includes(state),
    )
  }

  get currentPage() {
    return this.paginationData && this.paginationData.currentPage
  }

  get totalPages() {
    return this.paginationData && this.paginationData.totalPages
  }

  get showPaginationSummary() {
    return this.paginationData && this.paginationData.currentPage > 1
  }

  campsiteToCampsitePriceCardProps(campsite: CampsiteResult) {
    return {
      ...campsite,
      hierarchyStringShort: campsite.hierarchyStringShortCountry,
      primaryPhoto: campsite.primaryPhoto,
      isPointSearch: this.isPointSearch,
      searchFilters: this.searchFilters,
    }
  }

  previousY: number = 0
  previousRatio: number = 0
  visibilityChanged(isVisible, entry) {
    // Don't execute if component is unmounting or not mounted
    if (this._isUnmounting || !this._isMounted) {
      return
    }

    if (this.$isDesktop) {
      return
    }

    const currentY = entry.boundingClientRect.y
    const currentRatio = entry.intersectionRatio
    const isIntersecting = entry.isIntersecting

    if (isVisible) {
      this.$emit('visibleCampsiteChange', {
        slug: entry.target.dataset.slug,
        point: entry.target.dataset.point,
      })
    }

    if (currentY < this.previousY) {
      if (currentRatio > this.previousRatio && isIntersecting) {
        this.$emit('scrolledResultsDown', false)
      }
    } else if (
      currentY > this.previousY &&
      isIntersecting &&
      currentRatio > this.previousRatio
    ) {
      this.$emit('scrolledResultsDown', true)
    }
    this.previousY = currentY
    this.previousRatio = currentRatio
  }

  get head() {
    if (!this.paginationData) {
      return {}
    }

    const getAddressParts = (hierarchyString: string | undefined) => {
      if (!hierarchyString) return { locality: '', region: '', country: '' }
      const parts = hierarchyString.split(',').map((part) => part.trim())
      return {
        locality: parts[0] || '',
        region: parts[1] || '',
        country: parts[2] || '',
      }
    }

    return {
      script: [
        {
          type: 'application/ld+json',
          body: true,
          json: {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            url: `https://${this.$config.public.envUrl}${this.$route.path}`,
            itemListElement: this.campsites
              .map((campsite, index) => {
                const pageIndexBase =
                  ((this.paginationData?.currentPage || 1) - 1) *
                  (this.searchFilters.limit || defaultPageSize)
                // Skip campsites with invalid hierarchyPath to prevent errors
                if (
                  !campsite.hierarchyPath ||
                  typeof campsite.hierarchyPath !== 'string'
                ) {
                  console.error(
                    'SearchResultsList.head invalid hierarchyPath1',
                    campsite.slug,
                    campsite.hierarchyPath,
                  )
                  return null
                }
                const h = campsite.hierarchyPath.split('/')
                // Skip if required hierarchy params are missing
                if (!h[0] || !h[1] || !h[2]) {
                  console.error(
                    'SearchResultsList.head invalid hierarchyPath2',
                    campsite.slug,
                    campsite.hierarchyPath,
                  )
                  return null
                }
                const url = this.$puLocalePath({
                  name: 'campsite-detail',
                  params: {
                    h1: h[0],
                    h2: h[1],
                    h3: h[2],
                    h4: h[3],
                    slug: campsite.slug,
                  },
                })
                const addressParts = getAddressParts(
                  campsite.hierarchyStringShortCountry,
                )
                // Use schemaPhoto for structured data if available, otherwise fall back to primaryPhoto
                const schemaImageUrl =
                  campsite.schemaPhoto?.url.masterImage ||
                  campsite.primaryPhoto?.url.masterImage
                return {
                  '@type': 'ListItem',
                  position: index + 1 + pageIndexBase,
                  item: {
                    '@type': 'LodgingBusiness',
                    name: campsite.name,
                    url: `https://${this.$config.public.envUrl}${url}`,
                    address: {
                      '@type': 'PostalAddress',
                      streetAddress:
                        (campsite.address1 || '') +
                        ' ' +
                        (campsite.address2 || ''),
                      postalCode: campsite.postcode || '',
                      addressLocality: addressParts.locality,
                      addressRegion: addressParts.region,
                      addressCountry:
                        campsite.countryIso || addressParts.country,
                    },
                    image: schemaImageUrl
                      ? `https://${this.$config.public.envUrl}${schemaImageUrl}`
                      : `https://${this.$config.public.envUrl}/app-icons/pitchup_logo_og.png`,
                    aggregateRating:
                      campsite.rateCount && campsite.rating
                        ? {
                            '@type': 'AggregateRating',
                            ratingValue: parseFloat(
                              (campsite.rating * 2).toFixed(1),
                            ),
                            reviewCount: campsite.rateCount,
                            worstRating: '1',
                            bestRating: '10',
                          }
                        : undefined,
                    priceRange: campsite.leadPrice?.currency
                      ? this.currencyStore.currencies[
                          campsite.leadPrice.currency
                        ]?.symbol || campsite.leadPrice.currency
                      : '',
                    ...(campsite.bookable
                      ? {
                          amenityFeature: {
                            '@type': 'LocationFeatureSpecification',
                            name: 'instantBookable',
                            value: true,
                          },
                        }
                      : {}),
                  },
                }
              })
              .filter(Boolean), // Remove null entries for campsites with invalid hierarchyPath
          },
        },
      ],
    }
  }

  beforeUnmount() {
    // Set flags to prevent any async updates
    this._isUnmounting = true
    this._isMounted = false

    // Clean up hover timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
      this.hoverTimeout = null
    }

    // Clean up idle callback
    if (this.hoverIdleCallbackId) {
      cancelIdleCallback(this.hoverIdleCallbackId)
      this.hoverIdleCallbackId = null
    }

    // Clear any pending hover state
    this.currentHoverCampsite = null
    this.hoverCooldown = false

    // Clear visible campsites set
    this.visibleCampsites.clear()
  }
}

export default toNative(SearchResultsList)
</script>

<style scoped>
ul {
  margin: 0 4%;
  width: 92%;
}

a {
  text-decoration: none;
}

.search-card a {
  display: block;
}

.search-card:not(.basic-listing) + .search-card {
  content-visibility: auto;
  contain-intrinsic-size: 92vw 750px;
}

.basic-listing {
  content-visibility: auto;
  contain-intrinsic-size: 92vw 250px;
}

body:not(.user-dragging-gallery):not([data-desktop-search='true'])
  .search-card
  a:active,
.search-card-clicked {
  filter: brightness(0.8) drop-shadow(0 0.2em 0.3em #394a5a);
}

.search-card a:active :deep(.inline-gallery-loading-message) {
  display: none;
}

.search-results {
  background-color: var(--colour-page-background);
  padding-bottom: 1px;
  width: 100vw;
}

.search-results-list {
  min-height: 72vh;
}

.searching {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 72vh;
  width: 100%;
}

.searching span {
  font-size: var(--body);
  color: var(--colour-blue);
  margin-top: 1em;
}

.page-count-summary {
  color: var(--colour-wcag-blue);
  font-size: var(--small);
  padding: 0 4% 0.75em;
  margin-top: -0.25em;
}

.basic-listing-message {
  border-top: 4px solid white;
  color: var(--colour-wcag-blue);
  display: block;
  font-size: var(--body);
  margin: 0 -4%;
  padding: 1.5em 4%;
}

/* mobile landscape */
@media screen and (min-width: 320px) and (max-width: 998px) and (orientation: landscape) {
  .search-card a {
    display: grid;
    justify-content: center;
  }
}

/* desktop & tablet */
.desktop ul {
  margin: 0;
  width: 100%;
}

.search-results.desktop {
  background: var(--colour-page-background-light);
  height: auto;
  padding: 1em var(--page-width-padding) 2em;
  width: auto;
  position: relative;
}

.search-results.desktop.fetching .search-results-list {
  filter: blur(3px) contrast(0.85);
}

.search-results.desktop.fetching.no-search-cards {
  min-height: calc(100dvh - 28px);
}

.loading-spinner {
  display: none;
}

.search-results.desktop.fetching.no-search-cards .loading-spinner {
  display: flex;
  font-size: 2em;
  min-height: 50vh;
}

.loading-spinner :deep(svg) {
  margin: auto;
}

.search-results.desktop.fetching {
  cursor: progress;
}

.search-results.desktop.fetching::after {
  background-color: var(--colour-page-background-light);
  bottom: 0;
  content: '';
  cursor: wait;
  left: 0;
  opacity: 0.5;
  pointer-events: all;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
}

.desktop .search-results-list {
  background-color: transparent;
  --grid-layout-gap: calc(var(--page-width-padding) / 1.5);
  --grid-column-count: 6;
  --grid-item-min-width: 15em;
  --gap-count: calc(var(--grid-column-count) - 1);
  --total-gap-width: calc(var(--gap-count) * var(--grid-layout-gap));
  --grid-item-max-width: calc(
    (100% - var(--total-gap-width)) / var(--grid-column-count)
  );
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(max(var(--grid-item-min-width), var(--grid-item-max-width)), 1fr)
  );
  gap: clamp(1em, var(--grid-layout-gap), 1.7em);
  min-height: unset;
  transition: filter 0.2s ease-out;
}

.desktop .basic-listing-message {
  margin: 0;
  padding: 1.5em 0 0;
  column-span: all;
  grid-column: 1/-1;
}

.desktop .searching {
  min-height: auto;
}

.desktop .search-card {
  display: flex;
  flex-direction: column;
}

.desktop .search-card a {
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 30em;
}

.desktop .search-card a :deep(.album-indication) {
  display: none;
}

.desktop .search-card a :deep(.gallery-scrollbar) {
  margin: 0 0 0 1em;
}

.desktop .page-count-summary {
  padding: 0 0 1em;
  margin-top: 0;
}

.desktop .search-card:not(.basic-listing) + .search-card {
  content-visibility: visible;
  contain-intrinsic-size: auto 750px;
}

.desktop .basic-listing {
  content-visibility: auto;
  contain-intrinsic-size: auto 250px;
}
</style>
